import moment, { Moment } from "moment";
import React, { useCallback, useEffect, useState } from "react";
import { useFormContext, Controller, FieldValues } from "react-hook-form";

import { Validation } from "@/types/form";
import useClickOutside from "@/hooks/useClickOutSide";
import { ChevronLeft, ChevronRight } from "lucide-react";
import RenderLabel from "@/components/Form/Renderer/RenderLabel";
import Icon from "../ui/Image";
import { FieldErrorMessage, LightText2 } from "../ui/Text";
import { FORMATE } from "@/utils/constant/global.constant";
import { getNestedValue } from "@/utils/helper/form";

interface DateInputValidation extends Validation {
  minDate?: string;
  maxDate?: string;
  maxToday?: boolean;
}

interface DateRangeProps {
  label: string;
  field: string;
  fields: any[];
  placeholder: string;
  disabled?: boolean;
  validation: DateInputValidation;
  defaultValue?: { startDate: any; endDate: any };
  restrictFutureDates?: boolean;
}

const DateRange: React.FC<DateRangeProps> = ({ fields, field, disabled = false, validation, placeholder, defaultValue, restrictFutureDates = false }) => {
  const [currentDate, setCurrentDate] = useState<Moment>(moment());
  const [showDropdown, setDropdown] = useState("");

  const dropdownRef = useClickOutside(() => {
    setDropdown("");
  });

  const {
    control,
    setValue,
    getValues,
    trigger,
    formState: { errors },
  } = useFormContext<FieldValues>();
  const fieldError: any = errors;
  const minDate = moment("1980-01-01");
  const maxDate = moment("2100-12-31");

  const handleNavigation = (direction: "prev" | "next") => {
    setCurrentDate(direction === "next" ? moment(currentDate).add(1, "month") : moment(currentDate).subtract(1, "month"));
  };

  const isValidDate = (date: Moment, fieldName: string) => {
    // Check if future dates are restricted
    if (restrictFutureDates && date.isAfter(moment(), "day")) {
      return false;
    }
    
    if (fieldName.endsWith("startDate")) {
      return date.isSameOrAfter(minDate, "day") && date.isSameOrBefore(maxDate, "day");
    } else if (fieldName.endsWith("endDate")) {
      const startDate = moment(getValues(`${field}.startDate`));
      return date.isSameOrAfter(startDate, "day");
    }
    return true;
  };

  const generateCalendarDays = (fieldName: string) => {
    const firstDayOfMonth = moment(currentDate).startOf("month");
    const lastDayOfPrevMonth = moment(currentDate).subtract(1, "month").endOf("month");
    const startDay = firstDayOfMonth.day();

    const days = [];

    // Previous month days
    for (let i = startDay - 1; i >= 0; i--) {
      const prevDate = moment(lastDayOfPrevMonth).subtract(i, "days");
      days.push({
        date: lastDayOfPrevMonth.date() - i,
        isCurrentMonth: false,
        month: "prev",
        isValid: isValidDate(prevDate, fieldName),
      });
    }

    // Current month days
    const daysInMonth = currentDate.daysInMonth();
    for (let i = 1; i <= daysInMonth; i++) {
      const currentDay = moment(currentDate).date(i);
      days.push({
        date: i,
        isCurrentMonth: true,
        month: "current",
        isValid: isValidDate(currentDay, fieldName),
      });
    }

    // Next month days
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      const nextDate = moment(currentDate).add(1, "month").date(i);
      days.push({
        date: i,
        isCurrentMonth: false,
        month: "next",
        isValid: isValidDate(nextDate, fieldName),
      });
    }

    return days;
  };

  const dateValidationRules = (fieldName: string) => {
    return {
      required: true,
      validate: (value: Date) => {
        const val = moment(value);

        // Check if future dates are restricted
        if (restrictFutureDates && val.isAfter(moment(), "day")) {
          return "Future dates are not allowed";
        }

        if (fieldName.endsWith("startDate")) {
          const minDate = moment("1980-01-01");
          const maxDate = moment("2100-12-31");
          if (val.isBefore(minDate)) {
            return `Start Date must be after ${minDate.format(FORMATE.DATE)}`;
          }
          if (val.isAfter(maxDate)) {
            return `Start Date must be before ${maxDate.format(FORMATE.DATE)}`;
          }
        } else if (fieldName.endsWith("endDate")) {
          const startDate = moment(getValues(`${field}.startDate`));
          if (val.isBefore(startDate)) {
            return `End Date must be after ${startDate.format(FORMATE.DATE)}`;
          }
        }

        return true;
      },
    };
  };

  const startDate = getValues(`${field}.${fields[0]?.field as any}`);
  const endDate = getValues(`${field}.${fields[1]?.field as any}`);

  const formattedDuration = useCallback(() => {
    if (!startDate || !endDate) return null;
    const duration = moment.duration(moment(endDate).diff(moment(startDate)));
    const years = duration.years();
    const months = duration.months();
    return `${years ? `${years} Yr${years > 1 ? "s" : ""}` : ""} ${months ? `${months} Month${months > 1 ? "s" : ""}` : ""}`.trim();
  }, [startDate, endDate])();

  useEffect(() => {
    if (defaultValue?.startDate) {
      setValue(`${field}.${fields[0]?.field}`, defaultValue.startDate, { shouldDirty: false });
    }
    if (defaultValue?.endDate) {
      setValue(`${field}.${fields[1]?.field}`, defaultValue?.endDate, { shouldDirty: false });
    }
  }, [defaultValue?.startDate, defaultValue?.endDate]);

  return (
    <div className="relative w-full">
      <div className="flex flex-col text-xs">
        {/* Start Date Input */}
        <div className="flex gap-2 items-center w-full">
          {fields?.map((f: any, index: number) => {
            const fieldName = `${field}.${f?.field}`;
            let errorMessage = null;
            if (fieldError?.[field]?.[f?.field]) {
              errorMessage = fieldError?.[field]?.[f?.field]?.type;
            } else {
              errorMessage = "";
            }
            // console.log({ errorMessage, fieldError, F: f?.field });
            return (
              <div className="flex flex-col gap-1 w-full" key={fieldName}>
                <RenderLabel label={f?.label} className="" required={validation.required} />
                <Controller
                  name={fieldName}
                  control={control}
                  rules={dateValidationRules(fieldName)}
                  render={({ field: { value } }) => (
                    <div className="relative">
                      <input
                        type="text"
                        value={value ? moment(value).format(FORMATE.DATE) : ""}
                        onClick={() => {
                          // Set current date to the selected value's month, or current month if no value
                          if (value) {
                            setCurrentDate(moment(value));
                          } else {
                            setCurrentDate(moment());
                          }
                          setDropdown(fieldName);
                        }}
                        placeholder={placeholder}
                        readOnly
                        className={`w-full px-3 py-2 text-text-default rounded-lg outline-none border ${
                          errorMessage ? "border-text-error" : "border-fieldBorder"
                        } ${disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer"} hover:border-gray-400 focus:border-text-primary-500`}
                      />
                      <span className="absolute right-3 top-1/2 transform -translate-y-1/2 pointer-events-none">
                        <Icon src="/icons/calender.svg" alt="icon" />
                      </span>
                      {/* Calendar Dropdown */}
                      {showDropdown === fieldName && (
                        <div
                          className={`absolute top-full left-0 mt-2 w-full min-w-64 border rounded-lg shadow-calender z-50 bg-white  ${
                            index > 0 ? "-translate-x-12" : ""
                          }`}
                          ref={dropdownRef}
                        >
                          {/* Calendar Header */}
                          <div className="flex items-center justify-between mb-4 border-b-1 p-2 py-4">
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                handleNavigation("prev");
                              }}
                              className="hover:bg-gray-100"
                            >
                              <ChevronLeft className="text-icon-default2 size-4" />
                            </button>
                            <div className="flex justify-center flex-1 gap-2 flex-wrap">
                              <div className="flex items-center gap-2">
                                <select
                                  value={currentDate.year()}
                                  onChange={(e) => setCurrentDate(moment(currentDate).year(parseInt(e.target.value)))}
                                  className="w-fit px-2 py-0.5 text-text-default text-xs rounded-lg outline-none border border-fieldBorder hover:border-gray-400 focus:border-text-primary-500"
                                >
                                  {Array.from(
                                    {
                                      length:
                                        moment().year() -
                                        moment(index === 0 ? minDate : getNestedValue(getValues(field), "startDate")).year() +
                                        (index === 1 ? 150 : 1),
                                    },
                                    (_, i) => moment(index === 0 ? minDate : getNestedValue(getValues(field), "startDate")).year() + i
                                  ).map((year) => (
                                    <option key={year} value={year}>
                                      {year}
                                    </option>
                                  ))}
                                </select>
                                <select
                                  value={currentDate.month()}
                                  onChange={(e) => setCurrentDate(moment(currentDate).month(parseInt(e.target.value)))}
                                  className="w-fit px-2 py-0.5 text-text-default text-xs rounded-lg outline-none border border-fieldBorder hover:border-gray-400 focus:border-text-primary-500"
                                >
                                  {moment.months().map((month, index) => (
                                    <option key={month} value={index}>
                                      {month}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                            <button
                              onClick={(e) => {
                                e.preventDefault();
                                handleNavigation("next");
                              }}
                              className="hover:bg-gray-100"
                            >
                              <ChevronRight className="text-icon-default2 size-4" />
                            </button>
                          </div>

                          {/* Calendar Grid */}
                          <div className="grid grid-cols-7 gap-1 p-2">
                            {["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"].map((day) => (
                              <div key={day} className="text-center font-medium py-1 text-gray-600">
                                {day}
                              </div>
                            ))}
                            {generateCalendarDays(fieldName).map((day, index) => (
                              <button
                                key={index}
                                disabled={!day.isValid}
                                onClick={() => {
                                  const newDate = moment(currentDate);
                                  if (day.month === "prev") {
                                    newDate.subtract(1, "month");
                                  } else if (day.month === "next") {
                                    newDate.add(1, "month");
                                  }
                                  newDate.date(day.date);
                                  setValue(fieldName, newDate.format("YYYY-MM-DD"), { shouldDirty: true });
                                  trigger(fieldName);
                                  setDropdown("");
                                }}
                                className={`p-2 text-center ${!day.isCurrentMonth ? "text-gray-400" : "text-gray-700"} ${
                                  !day.isValid ? "opacity-50 cursor-not-allowed bg-gray-100" : ""
                                } ${
                                  moment(value).isSame(moment(currentDate).clone().set("date", day.date), "day") && day.isCurrentMonth
                                    ? "bg-purple-100 text-purple-600"
                                    : "hover:bg-gray-100"
                                }`}
                              >
                                {day.date}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                />
              </div>
            );
          })}
        </div>
        {fieldError?.[field] && <FieldErrorMessage text={"This field is required."} />}
        {formattedDuration && <LightText2 className="pt-1 !text-10" text={formattedDuration} />}
      </div>
    </div>
  );
};

export default DateRange;
